import React from 'react';
import { Ship, Footprints, Binoculars, Waves, Mountain } from 'lucide-react';

const TouristSpots = () => {
  const spots = [
  {
    id: 'jungle-trekking',
    title: 'Jungle Trekking',
    icon: Footprints,
    image: '/assets/images/Jungle_Trekking-1769668655331.JPG',
    imageAlt: 'Eco-friendly wooden jungle boardwalk trail winding through pristine tropical rainforest with lush green vegetation, sustainable tourism infrastructure at PPSRNP',
    description: 'The Jungle Trail is eco-tourism situated within the Puerto Princesa Subterranean River National Park. This guided nature trekking activity offers a scenic 5-kilometer journey that features diverse biodiversity including endemic flora and fauna, limestone formations and forest landscape.',
    details: 'Rehabilitated and officially reopened in January 2025 the Jungle Trail serves as an alternative trekking route to the Puerto Princesa Underground River while promoting conservation efforts and supporting local livelihood.'
  },
  {
    id: 'underground-river',
    title: 'Underground River Tour',
    icon: Ship,
    image: '/assets/images/Underground_River_Tours-1769668769003.jpg',
    imageAlt: 'Breathtaking view of the iconic Puerto Princesa Underground River entrance perfectly framed by pristine tropical vegetation with visitors in boats embarking on their legendary cave adventure through turquoise waters',
    description: 'Discover the Puerto Princesa Underground River on a guided paddle boat tour along a breathtaking underground river featuring remarkable limestone formations, stalactites and stalagmites.',
    details: 'A navigable river stretching 8.2 kilometers, flowing directly into the sea. The regular underground river tour covers approximately 1.2 kilometers, while the extended tour spans about 4.3 kilometers. The experience offers a memorable eco-adventure that highlights Palawan\'s exceptional natural beauty while supporting environmental conservation and sustainable tourism.'
  },
  {
    id: 'wildlife-watching',
    title: 'Wildlife Watching',
    icon: Binoculars,
    image: '/assets/images/ZZ_Wildlife_Watching-1769668898779.jpg',
    imageAlt: 'Blue-headed Racket-tail parrot showing distinctive metallic blue-green plumage and striking red eye photographed by Erickson A. Tabayag in 2014',
    description: 'Experience the rich biodiversity of PPSRNP with guided wildlife watching tours. Observe endemic and endangered species in their natural habitat, including the Philippine Cockatoo, Water Monitor Lizard, and various bird species.',
    details: 'The park contains 269 terrestrial vertebrate species with 70 endemic species. Birds comprise the largest group of vertebrates found in the park, with 5 threatened species listed under CITES.'
  },
  {
    id: 'mangrove-tour',
    title: 'Mangrove Forest Tour',
    icon: Waves,
    image: '/assets/images/Mangrove_Forest_Tour-1769667662146.jpg',
    imageAlt: 'Traditional wooden boats operated by local community members navigating through pristine mangrove-lined river channels, showcasing sustainable community-based tourism and conservation partnership',
    description: 'The Mangrove Paddle Boat Tour is a community-based sustainable tourism (CBST) managed by local residents, ensuring that tourism-generated benefits are retained within the host community.',
    details: 'The activity showcases the rich biodiversity of the mangrove forest while actively promoting environmental conservation. The guided paddle boat tour allows visitors to navigate through ancient mangrove and gain valuable knowledge about the mangrove ecosystem and its ecological importance.'
  },
  {
    id: 'beaches',
    title: 'Sabang, Panaguman & Marta Fe Beaches',
    icon: Waves,
    image: 'https://images.unsplash.com/photo-1619341374997-13a03b09cf07',
    imageAlt: 'Stunning white sand beach with crystal-clear turquoise waters lapping at shore, dramatic limestone karst formations rising from sea, pristine coastal paradise showcasing PPSRNP marine and terrestrial ecosystem convergence',
    description: 'Explore the stunning beaches of PPSRNP including Sabang Beach, Panaguman Beach, and Marta Fe Beach. Each offers unique coastal experiences with pristine waters and dramatic limestone formations.',
    details: 'These beaches provide perfect spots for relaxation, swimming, and enjoying the natural beauty of the West Philippine Sea coastline.'
  },
  {
    id: 'adventure-caving',
    title: 'Adventure Caving',
    icon: Mountain,
    image: '/assets/images/ZZ_Adventure_Caving-1769668265029.JPG',
    imageAlt: 'Adventurous kayakers paddling through the mystical underground river cave system with dramatic stalactites and stalagmites illuminated by headlamps, showcasing the legendary 8.2-kilometer subterranean journey through ancient limestone chambers',
    description: 'For the adventurous, PPSRNP offers exciting caving experiences at multiple sites including Ugong Rock, Lions Cave, Daylight Hole, and Kawili Cave.',
    details: 'Each cave system offers unique geological formations and challenges for different skill levels. Guided tours ensure safety while exploring these natural wonders.'
  }];


  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-4">
            Tourist Spots & Activities
          </h2>
          <p className="text-lg text-gray-800 dark:text-gray-200 max-w-3xl mx-auto">
            Explore the diverse eco-tourism experiences that PPSRNP has to offer, from underground river tours to jungle trekking and wildlife watching.
          </p>
        </div>

        {/* Spots Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {spots?.map((spot) => {
            const IconComponent = spot?.icon;
            return (
              <div
                key={spot?.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-[var(--color-border)] group">

                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={spot?.image}
                    alt={spot?.imageAlt}
                    className={`w-full h-full ${spot?.id === 'adventure-caving' ? 'object-contain bg-gray-100 dark:bg-gray-700' : 'object-cover'} group-hover:scale-110 transition-transform duration-300`} />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-full bg-white/90 dark:bg-gray-800/90 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[var(--color-primary)] dark:text-[var(--color-tertiary)]" />
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-3">
                    {spot?.title}
                  </h3>
                  <p className="text-gray-800 dark:text-gray-200 mb-3 leading-relaxed text-sm">
                    {spot?.description}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {spot?.details}
                  </p>
                </div>
              </div>);

          })}
        </div>
      </div>
    </section>);

};

export default TouristSpots;